import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { ITaggingFlowOption } from 'interfaces/tagging/ITaggingFlowOption';
import { TaggingSteps } from 'enums/TaggingSteps';
import { Play } from 'models/tagging/Play';
import { useInjection } from 'services/ioc/ioc.react';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ITaggingFlowState } from 'services/helper-services/TaggingFlowStateMachine';
import { constants, ResultTypes, IApiPlayerExtended } from '@trackman/baseball-shared-components';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ISequence } from 'interfaces/tagging/ISequence';
import { optionValueType } from 'services/helper-services/TaggingFlowStateMachineSteps';
import { getPlayersBySequenceNumber } from 'services/helper-services/mappingHelpers/sequenceMappings';
import { BaseState } from 'models/baseRunners/BaseState';
import { BaseRunners } from 'models/baseRunners/BaseRunners';
import { SessionStore } from 'stores/SessionStore';
import { ITagCatcherThrows } from 'interfaces/tagging/ITag';
import SmallExtraInningRuleSwitch from 'components/extraInningRuleSwitch/SmallExtraInningRuleSwitch';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { EndOfTaggingFlowCard } from '../EndOfTaggingFlowCard/EndOfTaggingFlowCard';
import { PitchTypeToggle } from './components/PitchTypeToggle/PitchTypeToggle';
import { TaggingFlowPlaceholder } from './components/Placeholder/TaggingFlowPlaceholder';
import { CatcherThrowsFlow } from './components/CatcherThrowsFlow/CatcherThrowsFlow';
import { Option } from './components/Option/Option';
import { SectionHeadline } from '../SectionHeadline/SectionHeadline';
import BaseRunnersFlow from './components/BaseRunnersFlow/BaseRunnersFlow';
import FieldingSequenceFlow from './components/FieldingSequenceFlow/FieldingSequenceFlow';
import OptionsBottomBar from './components/OptionsBottomBar/OptionsBottonBar';
import { Header, Options, Switches, TaggingColumn, TaggingFlowWrapper } from './TaggingFlowStyles';

export interface IProps {
  selectedPlay: Play | undefined;
  review: boolean;
  disabled?: boolean;
}

export const TaggingFlow: React.FC<IProps> = observer((props: IProps) => {
  const ManualPitchTypeKey = 'manual-pitch-type';
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const { playStore } = taggingStore;
  const { selectedPlay, review, disabled } = props;
  const [isManualPitchType, setIsManualPitchType] = useState<boolean>(true);
  const [state, setState] = useState<ITaggingFlowState>();

  useEffect(() => {
    handleManualPitchType(isManualPitchType);
  }, [selectedPlay]);

  useEffect(() => {
    selectedPlay?.flowStateMachine.setIsPlayerTrackingEnabled(sessionStore.currentSessionMetadata.isPlayerTrackingEnabled);
  }, [state]);

  useEffect(() => {
    const item = localStorage.getItem(ManualPitchTypeKey);
    if (item) {
      const manualPitchType = item === 'true';
      setIsManualPitchType(manualPitchType);
      handleManualPitchType(manualPitchType);
    } else {
      handleManualPitchType(false);
    }
  }, []);

  const handleManualPitchType = (manualPitchType: boolean): void => {
    if (!selectedPlay) {
      return;
    }
    if (manualPitchType && state?.name === TaggingSteps.Call) {
      selectedPlay.flowStateMachine.unskipPitchTypeStep();
    } else if (!manualPitchType) {
      selectedPlay && selectedPlay.flowStateMachine.skipPitchTypeStep();
    }
    setState(selectedPlay?.flowStateMachine.getCurrentState());
  };

  const onManualPitchTypeChange = (newVal: boolean): void => {
    localStorage.setItem(ManualPitchTypeKey, newVal.toString());
    setIsManualPitchType(newVal);
    handleManualPitchType(newVal);
  };

  const onSequenceChange = (sequence: ISequence[]) => {
    let sequenceBreadcrumbTitle = 'First player in sequence?';

    if (sequence[0]) {
      sequenceBreadcrumbTitle = `${sequence.join(' - ')} - Second player in sequence?`;
    }

    if (sequence[1]) {
      sequenceBreadcrumbTitle = `${sequence.join(' - ')} - Third player in sequence?`;
    }

    if (sequence[2]) {
      sequenceBreadcrumbTitle = sequence.join(' - ');
    }

    setState((prevState) => {
      if (prevState) {
        return {
          ...prevState,
          breadcrumbTitle: sequenceBreadcrumbTitle,
        };
      }
    });
  };

  const handleFlowStateChange = (value: optionValueType, displayValue: string): void => {
    if (disabled || !selectedPlay || !state) {
      return;
    }
    const { newState, updateFunction } = selectedPlay.flowStateMachine.changeState(value, displayValue);
    updateFunction(selectedPlay, value, displayValue);
    setState(newState);
    if (!review) {
      playStore.updateBallsAndStrikes();
    }
  };

  const saveFieldingSequence = (sequence: ISequence[]) => {
    if (selectedPlay) {
      const playersSequence: IApiPlayerExtended[] = getPlayersBySequenceNumber(sequence, selectedPlay);

      handleFlowStateChange(playersSequence, sequence.join(' - '));
    }
  };

  const handleOnClickBreadcrumb = (step: TaggingSteps): void => {
    if (!selectedPlay) {
      setState(undefined);
      return;
    }
    const { newState, updateFunction } = selectedPlay.flowStateMachine.resetState(step);
    updateFunction(selectedPlay);
    setState(newState);
    if (!review) {
      playStore.updateBallsAndStrikes();
    }
  };

  const isOptionDisabled = (step: TaggingSteps, option: ITaggingFlowOption): boolean => {
    return selectedPlay?.tag.playResult === ResultTypes.Out && step === TaggingSteps.Outs && option.value === 0;
  };

  const exitBaseRunners = () => {
    selectedPlay?.flowStateMachine.abortChangesAndBackToInterruptedStep();
    setState(selectedPlay?.flowStateMachine.getCurrentState());
  };

  const exitFieldingSequence = () => {
    selectedPlay?.flowStateMachine.abortChangesAndBackToInterruptedStep();
    setState(selectedPlay?.flowStateMachine.getCurrentState());
  };

  const saveBaseRunners = (bases: BaseState[]): void => {
    if (selectedPlay) {
      const battingTeamPlayers = selectedPlay.getBattingTeamLineup().players;
      // TODO: refactor base drop behaviour, avoid mapping players by their names, low quality fix below
      const basePlayers: BaseRunners = {
        firstBase:
          battingTeamPlayers.find(
            (player) =>
              player.id === bases.find((base) => base.id === 1)?.lastDroppedItem?.id ||
              player.nameRef === bases.find((base) => base.id === 1)?.lastDroppedItem?.name
          ) || null,
        secondBase:
          battingTeamPlayers.find(
            (player) =>
              player.id === bases.find((base) => base.id === 2)?.lastDroppedItem?.id ||
              player.nameRef === bases.find((base) => base.id === 2)?.lastDroppedItem?.name
          ) || null,
        thirdBase:
          battingTeamPlayers.find(
            (player) =>
              player.id === bases.find((base) => base.id === 3)?.lastDroppedItem?.id ||
              player.nameRef === bases.find((base) => base.id === 3)?.lastDroppedItem?.name
          ) || null,
      };

      handleFlowStateChange(basePlayers, 'Base Runners');
    }
  };

  const saveCatcherThrowValue = (value: ITagCatcherThrows, position: string) => {
    handleFlowStateChange(value, `2-${position}`);
    if (selectedPlay) {
      selectedPlay.saveCatcherThrow(true);
    }
  };

  const renderAdditionalStep = (currentStep: TaggingSteps): JSX.Element | void => {
    if (currentStep === TaggingSteps.CatcherThrows) {
      return (
        <CatcherThrowsFlow
          play={selectedPlay}
          onClickCallback={(value, position) => saveCatcherThrowValue(value, position)}
          onPressHotkeyCallback={(value, position) => saveCatcherThrowValue(value, position)}
          review={review}
        />
      );
    }
    if (currentStep === TaggingSteps.BaseRunners) {
      if (selectedPlay) {
        return (
          <BaseRunnersFlow
            key={1}
            exitBaseRunners={exitBaseRunners}
            saveBaseRunners={saveBaseRunners}
            baseRunners={{
              firstBase: selectedPlay?.baseRunners.firstBase || null,
              secondBase: selectedPlay?.baseRunners.secondBase || null,
              thirdBase: selectedPlay?.baseRunners.thirdBase || null,
            }}
            changedManually={selectedPlay?.isBRChangedManually}
            review={review}
            selectedPlay={selectedPlay}
          />
        );
      }
    }

    if (currentStep === TaggingSteps.FieldingSequence) {
      return (
        <FieldingSequenceFlow
          exitSequenceCallback={exitFieldingSequence}
          saveFieldingSequence={saveFieldingSequence}
          onSequenceChange={onSequenceChange}
          savedSequence={selectedPlay?.fieldingSequence || []}
          review={review}
        />
      );
    }
  };

  const renderOptions = (): JSX.Element | void => {
    if (
      !state ||
      state.name === TaggingSteps.CatcherThrows ||
      state.name === TaggingSteps.BaseRunners ||
      state.name === TaggingSteps.FieldingSequence
    ) {
      return;
    }

    return (
      <Options>
        {[...state.options, ...(state.automaticOptions || [])].map((option, index) => {
          return (
            <Option
              key={index}
              disabled={disabled || isOptionDisabled(state.name, option) || playStore.isPlaceholderPlaySelected}
              displayName={option.displayName}
              hotkeyCode={option.hotkeyCode}
              hotkeyDisplay={option.hotkeyDisplay}
              value={option.value}
              onPressHotkeyCallback={(value: string | number, displayValue: string) =>
                !taggingStore.isSubstituting && handleFlowStateChange(value, displayValue)
              }
              onClickCallBack={(value: string | number, displayValue: string) => handleFlowStateChange(value, displayValue)}
              data-test={`${option.value}-${index}`}
              tooltipContent={option.tooltipContent}
            />
          );
        })}
      </Options>
    );
  };

  const catcherThrowClickHandler = (): void => {
    if (!playStore.isPlaceholderPlaySelected && selectedPlay) {
      setState(selectedPlay.flowStateMachine.interruptState(TaggingSteps.CatcherThrows));
    }
  };

  const isCatcherThrowDisabled = (): boolean => {
    if (selectedPlay) {
      return (
        (selectedPlay.tag.catcherThrows?.thrownPosition && selectedPlay.tag.catcherThrows?.thrownPosition !== constants.undefinedValue) ||
        !!playStore.isPlaceholderPlaySelected
      );
    }
    return true;
  };

  const fieldingSequenceClickHandler = (): void => {
    if (!playStore.isPlaceholderPlaySelected && selectedPlay) {
      setState(selectedPlay.flowStateMachine.getPreviousState(TaggingSteps.FieldingSequence));
    }
  };

  const baseRunnersClickHandler = (): void => {
    if (!playStore.isPlaceholderPlaySelected && selectedPlay) {
      setState(selectedPlay.flowStateMachine.getPreviousState(TaggingSteps.BaseRunners));
    }
  };

  const shouldRenderSavedTagBadge = (): boolean => {
    if (selectedPlay) {
      return (
        !review &&
        (state?.name === TaggingSteps.Final || (!selectedPlay.isChanged && selectedPlay.isValidated)) &&
        state?.name !== TaggingSteps.BaseRunners &&
        state?.name !== TaggingSteps.FieldingSequence &&
        state?.name !== TaggingSteps.CatcherThrows
      );
    }

    return false;
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <TaggingFlowWrapper
        className={`${state?.hasCatcherThrows ? 'tag-catcher-throws-visible base-runners-visible fielding-sequence-visible' : ''}`}
      >
        {!review && (
          <Header>
            <SectionHeadline>Tagging</SectionHeadline>
            <Switches>
              <SmallExtraInningRuleSwitch />
              <PitchTypeToggle
                disabled={state?.name === TaggingSteps.CatcherThrows}
                isManualPitchType={isManualPitchType}
                isManualPitchTypeCallback={onManualPitchTypeChange}
              />
            </Switches>
          </Header>
        )}
        <TaggingColumn className='tagging-column'>
          <SectionHeader
            title={state?.breadcrumbTitle ?? ''}
            isBreadcrumb
            breadcrumbs={state?.breadcrumbs.filter((b) => isManualPitchType || b.step !== TaggingSteps.Pitch) ?? []}
            onClickBreadcrumbCallback={(value): void => handleOnClickBreadcrumb(value)}
          />
          {!selectedPlay ? (
            <TaggingFlowPlaceholder />
          ) : (
            <>
              {renderOptions()}
              {shouldRenderSavedTagBadge() && <EndOfTaggingFlowCard isTagSaved={!selectedPlay.isChanged && selectedPlay.isTagStored} />}
              {renderAdditionalStep(selectedPlay?.flowStateMachine.getCurrentState().name)}
            </>
          )}
        </TaggingColumn>
        <OptionsBottomBar
          catcherThrowClickHandler={catcherThrowClickHandler}
          isCatcherThrowDisabled={isCatcherThrowDisabled()}
          fieldingSequenceClickHandler={fieldingSequenceClickHandler}
          isFieldingSequenceDisabled={selectedPlay ? selectedPlay.isFieldingSequenceCompleted : true}
          baseRunnersClickHandler={baseRunnersClickHandler}
          isBaseRunnersDisabled={selectedPlay ? selectedPlay.isBaseRunnersCompleted : true}
          state={state}
          isPlayerTrackingEnabled={sessionStore.currentSessionMetadata.isPlayerTrackingEnabled}
        />
      </TaggingFlowWrapper>
    </DndProvider>
  );
});
